import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ResetPasswordPage: React.FC = () => {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Could not reset password. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ground">
      <div className="max-w-md w-full mx-4 sm:mx-auto space-y-8 p-6 sm:p-8 bg-white rounded-xl shadow-overlay">
        {/* Logo */}
        <div className="flex flex-col items-center cursor-pointer gap-2" onClick={() => navigate('/')}>
          <img src="/dig_no_name_logo.png" alt="dialledin.golf" className="h-20 w-auto object-contain" />
          <img src="/dig_name_only_white_line.png" alt="dialledin.golf" className="h-10 w-auto object-contain" />
          <p className="text-ink-3 text-sm mt-1">A machine measures your swing; we recommend clubs built for it</p>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 text-center">Set a new password</h1>

        {success ? (
          <div className="space-y-6">
            <div className="bg-green/10 border border-green-ink text-green-ink px-4 py-3 rounded-lg text-sm">
              Password updated successfully. You can now log in.
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 text-white font-medium rounded-lg bg-green-ink hover:opacity-90 active:bg-green-ink transition-colors"
            >
              Go to Login
            </button>
          </div>
        ) : !token ? (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              This reset link is missing its token. Please request a new one.
            </div>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="w-full py-3 px-4 text-white font-medium rounded-lg bg-green-ink hover:opacity-90 active:bg-green-ink transition-colors"
            >
              Request New Link
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password *
              </label>
              <input
                id="newPassword"
                type="password"
                required
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                placeholder="••••••••"
              />
              <p className="mt-2 text-sm text-gray-500">Must be at least 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password *
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 text-white font-medium rounded-lg transition-colors ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-ink hover:opacity-90 active:bg-green-ink'
              }`}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}

        {!success && !!token && (
          <div className="text-center text-sm text-gray-600">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-green-ink hover:text-green-ink font-medium"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
